import { notEmpty, theme } from "@product/scmp-sdk";
import type { FunctionComponent } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { graphql, useFragment } from "react-relay";

import { useAppBarStickyHeightValue } from "~/components/app-bar/hooks";
import { ArticleImageGallery } from "~/components/article/article-image-gallery";
import { ImageSize } from "~/components/article/article-leading/article-leading-images/enums";
import type { ArticleRenderChildProps } from "~/components/article/article-render";
import { GlobalChecker } from "~/components/article/article-render/global-checker";
import { useReduceWhitespaceByAvailableSpace } from "~/components/article/article-render/hooks";
import { LiveArticleNewsletterWidget } from "~/components/article/article-render/live-article/newsletter-widget";
import { ClientSideSuspense } from "~/components/common/client-side-suspense";
import { sendGA4Tracking } from "~/components/tracking/google-analytics-4/apis";
import type { liveArticleArticle$key } from "~/queries/__generated__/liveArticleArticle.graphql";
import type { liveContentLoaderQuery$data } from "~/queries/__generated__/liveContentLoaderQuery.graphql";

import { useLiveArticleScrollContext } from "./contexts";
import { makeSureContentItemExists } from "./helpers";
import { withLiveArticleScrollContext } from "./hoc";
import { useDeepLinkLocator, useDetectComponentRender, useLiveContentSubscription } from "./hooks";
import { LiveContentLoader } from "./live-content-loader";
import { LiveArticleLiveUpdateSwitch } from "./live-update-switch";
import {
  Author,
  CommentAndReaction,
  Content,
  DesktopHeaderAdSlot,
  Follow,
  Headline,
  Introduction,
  Leading,
  Left,
  LiveArticleContainer,
  LiveSubscribe,
  LiveUpdateAlert,
  LiveUpdateAlertContainer,
  MobileContentFooterAdSlot,
  MobileContentHeaderAdSlot,
  MobileFooterAdSlot,
  MobileFooterNewsletter,
  MoreOnThis,
  NewsletterWidgetContainer,
  RightAvailableSpaceTracker,
  Section,
  SharePanel,
  Sponsor,
  StyledArticleContentRightSection,
  StyledLiveArticleContent,
  StyledLiveArticleSummary,
  StyledLiveArticleTimeline,
  SubHeadline,
  TimelineContainer,
  Topic,
} from "./styles";
import type { EntityIdUuidMapEntry } from "./types";

export type Props = {
  reference: liveArticleArticle$key;
} & ArticleRenderChildProps;

const Component: FunctionComponent<Props> = ({
  isInlinePaywallDomChanged,
  isShowInlinePaywall,
  provider,
  reference,
}) => {
  const article = useFragment(
    graphql`
      fragment liveArticleArticle on Article {
        entityId
        entityUuid
        ...articleImageGalleryContent
        ...contentLiveArticleArticle
        ...globalCheckerContent
        ...newsletterWidgetLiveArticleArticle
        ...reduceWhitespaceByAvailableSpaceArticle
      }
    `,
    reference,
  );

  const [data, setData] = useState<liveContentLoaderQuery$data>();
  const [entityIdUuidMap, setEntityIdUuidMap] = useState<Record<string, EntityIdUuidMapEntry>>({});

  const { captureElement, rendered } = useDetectComponentRender();
  const { appBarStickyHeight } = useAppBarStickyHeightValue();
  const { captureResetElement, setScrollItem } = useLiveArticleScrollContext();
  const { newUpdateCount, randomString, setNewUpdateCount, setShouldEnable } =
    useLiveContentSubscription(article.entityUuid, entityIdUuidMap ?? {});
  const {
    entityUuid: deepLinkEntityUuid,
    position: deepLinkPosition,
    reset: resetDeepLinkPosition,
  } = useDeepLinkLocator(entityIdUuidMap ?? {});

  const liveArticle = useMemo(() => data?.liveArticle ?? undefined, [data?.liveArticle]);

  const liveContents = useMemo(
    () => data?.liveArticle?.liveContents?.filter(notEmpty) ?? [],
    [data?.liveArticle?.liveContents],
  );

  const isTheEnd = useMemo(
    () => data?.liveArticle?.status === false && newUpdateCount === 0,
    [data?.liveArticle?.status, newUpdateCount],
  );

  const isShowContent = useMemo(
    () => !(isShowInlinePaywall ?? false) || (isInlinePaywallDomChanged ?? false),
    [isInlinePaywallDomChanged, isShowInlinePaywall],
  );

  const { reduceWhitespaceByAvailableSpaceSpec } = useReduceWhitespaceByAvailableSpace({
    isEnabled: isShowContent,
    isShowRightMoreOnThis: false,
    reference: article,
    specs: {
      adSlotSize: 600,
      nativeAdSlotSize: 260,
      whitespaceAllowance: 100,
    },
    trackers: {
      right: RightAvailableSpaceTracker,
    },
  });

  useEffect(() => {
    setShouldEnable(isShowContent);
  }, [isShowContent, setShouldEnable]);

  useEffect(() => {
    if (!notEmpty(deepLinkPosition)) return;
    setNewUpdateCount(deepLinkPosition);
  }, [deepLinkPosition, setNewUpdateCount]);

  useEffect(() => {
    if (
      !setScrollItem ||
      !notEmpty(deepLinkEntityUuid) ||
      Object.keys(entityIdUuidMap).length === 0
    )
      return;

    const run = async () => {
      await makeSureContentItemExists(deepLinkEntityUuid);
      setScrollItem(deepLinkEntityUuid);
    };
    void run();
  }, [deepLinkEntityUuid, entityIdUuidMap, setScrollItem]);

  const scrollToLatestContent = useCallback(() => {
    resetDeepLinkPosition();
    if (typeof window === "undefined" || !window.requestAnimationFrame) return;
    if (!setScrollItem) return;
    if (!notEmpty(entityIdUuidMap)) return;

    const [latest] = Object.values(entityIdUuidMap).sort((a, b) => b.customTime - a.customTime);

    const run = async () => {
      await makeSureContentItemExists(latest.entityUuid);
      setScrollItem(latest.entityUuid);
      setNewUpdateCount(0);
    };
    void run();
  }, [entityIdUuidMap, resetDeepLinkPosition, setNewUpdateCount, setScrollItem]);

  return (
    <LiveArticleContainer>
      <Section>{provider.sections()}</Section>
      <Headline ref={captureResetElement}>
        {provider.liveTag()}
        {provider.headline()}
      </Headline>
      <SubHeadline>{provider.subHeadline()}</SubHeadline>
      <Sponsor>{provider.sponsor()}</Sponsor>
      <Follow>{provider.topicFollowWidget({ variant: "banner" })}</Follow>
      <Author>{provider.authorBlock()}</Author>

      <Leading ref={captureResetElement}>
        <ArticleImageGallery reference={article}>
          {provider.leading({
            responsiveDescriptionVariants: {
              desktopUp: "show",
              mobileUp: "hidden",
              tabletUp: "show",
            },
            responsiveImageVariants: {
              desktopUp: "normal",
              mobileUp: "normal",
              tabletUp: "normal",
            },
            responsiveVariants: {
              desktopUp: ImageSize["1200x800"],
              mobileUp: ImageSize["1200x800"],
              tabletUp: ImageSize["1200x800"],
            },
          })}
        </ArticleImageGallery>
      </Leading>

      <LiveUpdateAlertContainer
        $isShow={newUpdateCount > 0}
        $stickyStackedHeight={appBarStickyHeight}
      >
        <LiveUpdateAlert
          onClick={() => {
            scrollToLatestContent();
            sendGA4Tracking({
              action: "click",
              category: "liveblog",
              customized_parameters: undefined,
              subcategory: "update",
            });
          }}
        >
          {newUpdateCount > 1 ? `${newUpdateCount} New Updates` : `${newUpdateCount} New Update`}
        </LiveUpdateAlert>
      </LiveUpdateAlertContainer>

      <Introduction>Introduction</Introduction>

      <Content ref={captureResetElement}>
        {provider.body({
          extraComponentMap: {
            "inline-widget": null,
          },
        })}
      </Content>

      <ClientSideSuspense>
        <GlobalChecker reference={article}>
          <ClientSideSuspense>
            <LiveContentLoader entityUuid={article.entityUuid} randomString={randomString}>
              {({ data, entityIdUuidMap }) => {
                setTimeout(() => {
                  setData(data);
                  setEntityIdUuidMap(entityIdUuidMap);
                });
              }}
            </LiveContentLoader>
          </ClientSideSuspense>

          {liveArticle && (
            <>
              <LiveSubscribe>
                <LiveArticleLiveUpdateSwitch entityId={article.entityId} reference={liveArticle} />
              </LiveSubscribe>

              <StyledLiveArticleContent
                articleReference={article}
                deepLinkPosition={deepLinkPosition}
                isTheEnd={isTheEnd}
                liveArticleReference={liveArticle}
                provider={provider}
                ref={captureElement}
              />
            </>
          )}

          {liveContents && (
            <>
              <StyledLiveArticleSummary reference={liveContents} />

              <TimelineContainer $stickyStackedHeight={appBarStickyHeight}>
                <StyledLiveArticleTimeline
                  deepLinkPosition={deepLinkPosition}
                  reference={liveContents}
                />
              </TimelineContainer>
            </>
          )}

          {rendered && (
            <>
              <CommentAndReaction>{provider.commentTriggerAndReaction()}</CommentAndReaction>
              <MoreOnThis>{provider.moreOnThis({ variant: "horizontal" })}</MoreOnThis>
              <Topic>{provider.topics()}</Topic>
            </>
          )}
        </GlobalChecker>
      </ClientSideSuspense>

      <StyledArticleContentRightSection
        {...reduceWhitespaceByAvailableSpaceSpec.right}
        entityId={article.entityId}
        provider={provider}
        rightSectionCustomSlot={
          <NewsletterWidgetContainer>
            <LiveArticleNewsletterWidget reference={article} />
          </NewsletterWidgetContainer>
        }
      />
      {reduceWhitespaceByAvailableSpaceSpec.right.trackerNode}

      <MobileFooterNewsletter>
        <LiveArticleNewsletterWidget reference={article} />
      </MobileFooterNewsletter>

      <Left $stickyStackedHeight={appBarStickyHeight}>
        <SharePanel>
          {provider.shareWidget()}
          {provider.actionWidget()}
        </SharePanel>
      </Left>

      <DesktopHeaderAdSlot>
        {provider.adslot({
          adUnit: "d_banner1",
          breakpoint: theme.breakpoints.up("desktop"),
          sizes: [[970, 250], [728, 90], [970, 90], "fluid"],
        })}
      </DesktopHeaderAdSlot>

      <MobileContentHeaderAdSlot>
        {provider.adslot({
          adUnit: "m_banner1",
          breakpoint: theme.breakpoints.down("tablet"),
          sizes: [[300, 250], "fluid"],
          withBackground: true,
          withLabel: true,
        })}
      </MobileContentHeaderAdSlot>

      <MobileContentFooterAdSlot>
        {provider.adslot({
          adUnit: "native4",
          breakpoint: theme.breakpoints.down("tablet"),
          sizes: ["fluid"],
        })}
      </MobileContentFooterAdSlot>

      <MobileFooterAdSlot>
        {provider.adslot({
          adUnit: "m_lrec3a",
          breakpoint: theme.breakpoints.down("tablet"),
          sizes: [[300, 250], "fluid"],
          withBackground: false,
          withLabel: true,
        })}
      </MobileFooterAdSlot>
    </LiveArticleContainer>
  );
};

Component.displayName = "LiveArticle";

export const LiveArticle = withLiveArticleScrollContext(Component);
